import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePortal } from '../context/PortalContext';
import { useSupportContext } from '../context/SupportContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  Gavel,
  FileCheck,
  Upload,
  CheckSquare,
  Activity,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  Shield,
  Bell,
  Menu,
  X,
  Command,
  Scale,
  Radio,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import SupportModeBanner from '../components/platform/SupportModeBanner';
import CommandPalette from '../components/platform/CommandPalette';
import { getPortalFromHostname, PORTAL_SURFACES, isPlatformConsoleRole } from '../utils/hostnamePortal';
import FundEliteBrandMark from '../components/branding/FundEliteBrandMark';

/**
 * Staff Portal Layout - Phase 6 + Phase 9 Enhancements
 * 
 * Layout shell for staff users (owner, specialist, staff).
 * Shows full agency access, queues, and workflow tools.
 * 
 * Phase 9 additions:
 * - Accordion behavior (one group open at a time)
 * - Improved scroll handling
 * - Command palette support
 * - Premium support banner
 */

const NavItem = ({ icon: Icon, label, to, children, badge, openSection, onSectionToggle }) => {
  const location = useLocation();
  const hasChildren = children && children.length > 0;
  const isOpen = openSection === label;
  const isActive = to ? location.pathname.startsWith(to) : children?.some(c => location.pathname.startsWith(c.to));

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          onClick={() => onSectionToggle(isOpen ? null : label)}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all
            ${isActive 
              ? 'bg-white/15 text-white' 
              : 'text-white/70 hover:bg-white/10 hover:text-white'
            }`}
          data-testid={`staff-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-center gap-3">
            <Icon size={20} strokeWidth={1.5} />
            <span>{label}</span>
          </div>
          <div className="flex items-center gap-2">
            {badge && (
              <span className="px-2 py-0.5 text-xs bg-emerald-500 text-white rounded-full">{badge}</span>
            )}
            <ChevronDown 
              size={16} 
              className={`transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
            />
          </div>
        </button>
        {/* Animated accordion content */}
        <div 
          className={`overflow-hidden transition-all duration-200 ease-out ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="mt-1 ml-4 pl-4 border-l border-white/10">
            {children.map((child, index) => (
              <Link
                key={index}
                to={child.to}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all
                  ${location.pathname === child.to
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:bg-white/5 hover:text-white/90'
                  }`}
                data-testid={`staff-nav-sub-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span>{child.label}</span>
                {child.badge && (
                  <span className="px-2 py-0.5 text-xs bg-emerald-500/80 text-white rounded-full">{child.badge}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all mb-1
        ${isActive 
          ? 'bg-white/15 text-white' 
          : 'text-white/70 hover:bg-white/10 hover:text-white'
        }`}
      data-testid={`staff-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} strokeWidth={1.5} />
        <span>{label}</span>
      </div>
      {badge && (
        <span className="px-2 py-0.5 text-xs bg-emerald-500 text-white rounded-full">{badge}</span>
      )}
    </Link>
  );
};

const StaffPortalLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { capabilities } = usePortal();
  const { isInSupportMode, isPlatformOwner } = useSupportContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  useEffect(() => {
    if (isPlatformConsoleRole(user?.role) && !isInSupportMode) {
      navigate('/platform/dashboard', { replace: true });
    }
  }, [user?.role, isInSupportMode, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Auto-open section based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/clients') || path.includes('/staff/cases')) setOpenSection('Clients');
    else if (path.includes('/staff/queue/disputes') || path.includes('/staff/queue/letters')) setOpenSection('Dispute Queue');
    else if (path.includes('/credit-report')) setOpenSection('Credit Reports');
    else if (path.includes('/admin')) setOpenSection('Admin');
  }, [location.pathname]);

  // Global keyboard shortcut for Command Palette (Platform owners only)
  useEffect(() => {
    if (!isPlatformOwner) return;
    
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlatformOwner]);

  // Staff-specific navigation
  const navItems = [
    { 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      to: '/staff/dashboard',
    },
    {
      icon: Users,
      label: 'Clients',
      children: [
        { label: 'Cases', to: '/staff/clients/cases' },
        { label: 'Users', to: '/staff/clients/users' },
        { label: 'My Assigned', to: '/staff/clients/assigned' },
        { label: 'Unassigned', to: '/staff/clients/unassigned' },
      ],
    },
    {
      icon: Gavel,
      label: 'Dispute Queue',
      children: [
        { label: 'Active Disputes', to: '/staff/queue/disputes' },
        { label: 'Pending Letters', to: '/staff/queue/letters' },
      ],
    },
    {
      icon: FileCheck,
      label: 'Document Requests',
      to: '/staff/queue/document-requests',
    },
    {
      icon: Upload,
      label: 'Upload Review',
      to: '/staff/queue/uploads',
    },
    {
      icon: CheckSquare,
      label: 'Tasks',
      to: '/staff/queue/tasks',
    },
    {
      icon: Activity,
      label: 'Activity Feed',
      to: '/staff/activity',
    },
    {
      icon: Scale,
      label: 'Letter Markup',
      to: '/staff/collection-markup',
    },
    {
      icon: FileText,
      label: 'Credit Reports',
      children: [
        { label: 'Import Report', to: '/credit-report/import' },
        { label: 'Report Analysis', to: '/credit-report' },
      ],
    },
    {
      icon: Settings,
      label: 'Settings',
      children: [
        { label: 'Agency Settings', to: `/platform/agencies/${user?.agency_id}/settings` },
        { label: 'My Profile', to: '/settings/profile' },
        { label: 'Security', to: '/settings/security' },
      ],
    },
  ];

  // Add admin if user is owner/admin
  if (user?.role === 'owner' || user?.role === 'platform_admin') {
    navItems.push({
      icon: Shield,
      label: 'Admin',
      children: [
        { label: 'Overview', to: '/admin' },
        { label: 'Parser Training', to: '/admin/parser-training' },
        { label: 'Knowledge Base', to: '/admin/knowledge-base' },
      ],
    });
  }

  const showPlatformShortcuts =
    getPortalFromHostname() === PORTAL_SURFACES.ADMIN_AGENCY &&
    ['platform_owner', 'super_admin', 'platform_admin'].includes(user?.role);

  if (showPlatformShortcuts) {
    navItems.push({
      icon: Radio,
      label: 'Platform',
      children: [
        { label: 'Automations', to: '/platform/automations' },
        { label: 'Contacts', to: '/platform/contacts' },
        { label: 'Mail (Mailgun)', to: '/platform/settings/integrations/mailgun' },
        { label: 'Integrations', to: '/platform/settings/data/integrations' },
        { label: 'Platform settings', to: '/platform/settings' },
      ],
    });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Support Mode Banner - Premium Glassmorphism */}
      <SupportModeBanner />

      {/* Command Palette (Platform owners only) */}
      {isPlatformOwner && (
        <CommandPalette 
          isOpen={isCommandPaletteOpen} 
          onClose={() => setIsCommandPaletteOpen(false)} 
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-gradient-to-b from-stone-900 to-stone-800 transform transition-transform duration-300 ease-in-out flex flex-col
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="staff-sidebar"
      >
        {/* Logo - Fixed */}
        <div className="p-5 mb-2 flex-shrink-0">
          <Link to="/staff/dashboard" className="flex flex-col gap-1" data-testid="staff-sidebar-logo">
            <FundEliteBrandMark
              variant="agency-admin"
              mode="logo"
              logoClassName="h-8 w-auto max-w-[168px] object-contain object-left"
            />
            <p className="text-xs text-amber-400/80">Staff Portal</p>
          </Link>
        </div>

        {/* Navigation - Scrollable with hidden scrollbar */}
        <nav 
          className="flex-1 px-3 pb-4 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent"
          style={{ 
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(100,116,139,0.5) transparent'
          }}
        >
          {navItems.map((item, index) => (
            <NavItem
              key={index}
              icon={item.icon}
              label={item.label}
              to={item.to}
              children={item.children}
              badge={item.badge}
              openSection={openSection}
              onSectionToggle={setOpenSection}
            />
          ))}
          
          {/* Command Palette Trigger (Platform owners in agency context) */}
          {isPlatformOwner && (
            <button
              onClick={() => setIsCommandPaletteOpen(true)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-all mt-4 border border-white/10"
              data-testid="staff-cmd-k-trigger"
            >
              <div className="flex items-center gap-3">
                <Command size={18} />
                <span className="text-sm">Quick Search</span>
              </div>
              <kbd className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">⌘K</kbd>
            </button>
          )}

          {/* Admin Control Center Link - Only for authorized internal roles */}
          {['platform_owner', 'platform_admin', 'admin', 'owner', 'specialist'].includes(user?.role) && (
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-2 px-2">Internal Tools</p>
              <Link
                to="/admin"
                data-testid="staff-nav-admin-control"
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all border ${
                  location.pathname.startsWith('/admin')
                    ? 'bg-amber-600/30 text-amber-300 border-amber-500/50'
                    : 'bg-amber-600/10 text-amber-400 hover:bg-amber-600/20 border-amber-500/20'
                }`}
              >
                <div className="p-1.5 rounded-md bg-amber-500/20">
                  <Shield size={16} />
                </div>
                <div className="flex-1">
                  <span className="font-medium block text-sm">Admin Control</span>
                  <span className="text-xs text-white/40">AI Operations & Training</span>
                </div>
              </Link>
            </div>
          )}
        </nav>

        {/* User Card - Fixed at bottom */}
        <div className="p-4 border-t border-white/10 bg-stone-900/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-emerald-500/30">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-emerald-500/20 text-emerald-400 font-semibold">
                {user?.name?.charAt(0) || 'S'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name || 'Staff'}</p>
              <p className="text-xs text-white/50 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-white/50 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="staff-logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-[rgba(6,28,20,0.75)] z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div>
                <h2 className="text-lg font-semibold text-stone-900">Staff Portal</h2>
                <p className="text-sm text-slate-500">Manage clients and workflows</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="p-2 rounded-lg hover:bg-slate-100 relative">
                <Bell size={20} className="text-slate-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default StaffPortalLayout;
